import { type ListingLane, normalizeListingType, type RuntimeAdapterConfig } from "./config.ts";
import type { AdapterDefinition } from "./types.ts";

/**
 * The one rule for scrape_jobs.status once a run stops, shared by every
 * runtime that ends runs (job-orchestrator, discover-worker, process-queue,
 * the Firebase reconciler) and by the scheduler that picks due jobs.
 *
 * A scheduled job always goes back to 'scheduled' so execute-scheduled-job
 * and sync-cron-schedules keep seeing it; an immediate job keeps the run's
 * terminal status. Nothing here ever yields 'idle', which the scheduler
 * ignores — that is how job 31b020e0 fell off the schedule for four months.
 */

export type RunEndOutcome = "completed" | "failed" | "cancelled";

export interface JobScheduleFields {
  run_type?: string | null;
  cron_expression?: string | null;
}

export function isScheduledJob(job: JobScheduleFields): boolean {
  return job.run_type === "scheduled" && Boolean(job.cron_expression);
}

export function releasedJobStatus(
  job: JobScheduleFields,
  outcome: RunEndOutcome,
): "scheduled" | RunEndOutcome {
  return isScheduledJob(job) ? "scheduled" : outcome;
}

/**
 * A run with no activity for this long is dead. last_activity_at is bumped
 * by DB triggers on every queue status change and every counter or phase
 * change, so a live run moves it every few minutes; the reaper already calls
 * a run stuck at 10 minutes. Six hours leaves room for long provider
 * back-offs and a slow single discovery page chain, while still freeing a
 * weekly or daily job well before its next occurrence.
 */
export const STALE_RUN_AFTER_MS = 6 * 60 * 60 * 1000;

const OPEN_RUN_STATUSES = new Set(["running", "pending"]);

export interface OpenRunCandidate {
  status: string;
  started_at: string;
  last_activity_at?: string | null;
}

export function partitionOpenRuns<T extends OpenRunCandidate>(
  runs: T[],
  now: number = Date.now(),
): { active: T[]; stale: T[] } {
  const active: T[] = [];
  const stale: T[] = [];
  for (const run of runs) {
    if (!OPEN_RUN_STATUSES.has(run.status)) continue;
    const lastSeen = Math.max(
      Date.parse(run.started_at),
      Date.parse(run.last_activity_at ?? "") || 0,
    );
    (now - lastSeen > STALE_RUN_AFTER_MS ? stale : active).push(run);
  }
  return { active, stale };
}

function cronFieldMatches(field: string, value: number): boolean {
  if (field === "*") return true;
  if (field.includes(",")) {
    return field.split(",").some((part) => cronFieldMatches(part.trim(), value));
  }
  if (field.includes("/")) {
    const [base, step] = field.split("/");
    const start = base === "*" ? 0 : Number(base);
    return value >= start && (value - start) % Number(step) === 0;
  }
  if (field.includes("-")) {
    const [start, end] = field.split("-").map(Number);
    return value >= start && value <= end;
  }
  return Number(field) === value;
}

/**
 * Next time a 5-field cron expression fires after `from`, in UTC (pg_cron's
 * zone). Shared by job creation, the schedule editor and job-orchestrator so
 * scrape_jobs.next_run_at agrees with the pg_cron entry. An expression that is
 * not 5 fields, or never fires within 40 days, yields `from` + 1 day.
 */
export function nextCronRunAt(cronExpr: string, from: Date = new Date()): Date {
  const fallback = new Date(from.getTime() + 86_400_000);
  const parts = cronExpr.trim().split(/\s+/);
  if (parts.length !== 5) return fallback;
  const [minute, hour, day, month, weekday] = parts;
  const next = new Date(from);
  next.setUTCSeconds(0, 0);
  for (let i = 0; i < 40 * 24 * 60; i++) {
    if (
      next.getTime() > from.getTime() &&
      cronFieldMatches(minute, next.getUTCMinutes()) &&
      cronFieldMatches(hour, next.getUTCHours()) &&
      cronFieldMatches(day, next.getUTCDate()) &&
      cronFieldMatches(month, next.getUTCMonth() + 1) &&
      cronFieldMatches(weekday, next.getUTCDay())
    ) {
      return next;
    }
    next.setUTCMinutes(next.getUTCMinutes() + 1);
  }
  return fallback;
}

/** One discovery task's lane and the config its search URL is built from. */
export interface DiscoveryLanePlan {
  listingType: ListingLane;
  config: RuntimeAdapterConfig;
}

/**
 * The discovery lanes (discovery_tasks.listing_type) of one adapter in a run, each with its config.
 *
 * `requested` is the listing type the job and adapter config name, read before
 * normalizeAdapterConfig, which fills an unset type with buy: unset means Any here (review finding
 * 1; the job dialog stores Any as unset). A named buy, sold or rent is one lane, as before. Any or
 * unset is the adapter's anyListingTypes (REA and Domain: buy and sold), else sold. An adapter that
 * declares no listingTypes searches a fixed sold page and keeps its config as it is.
 */
export function planDiscoveryLanes(
  requested: string | undefined,
  config: RuntimeAdapterConfig,
  adapter: Pick<AdapterDefinition, "listingTypes" | "anyListingTypes"> | undefined,
): DiscoveryLanePlan[] {
  if (!adapter?.listingTypes?.length) return [{ listingType: "sold", config }];
  const named = normalizeListingType(requested);
  const lanes: readonly ListingLane[] =
    named && named !== "any" ? [named] : (adapter.anyListingTypes ?? ["sold"]);
  return lanes.map((listingType) => ({ listingType, config: { ...config, listingType } }));
}

interface WriteResult {
  error?: { code?: string; message?: string } | null;
}

/**
 * The database has no listing_type column yet: PostgREST's schema-cache miss (PGRST204) or
 * Postgres's undefined column (42703). CI applies migration 20261023090000 while the code that
 * writes the column deploys, so for a few minutes either can land first.
 */
export function isMissingListingTypeColumn(error: WriteResult["error"]): boolean {
  return (
    (error?.code === "PGRST204" || error?.code === "42703") &&
    (error.message ?? "").includes("listing_type")
  );
}

/**
 * Run a write that sets listing_type; when the column does not exist yet, call `onMissing` and run
 * it again without the column, so a task or queue row is never lost to deploy order. The row then
 * takes the column's 'sold' default once the migration lands, as rows from older code do.
 */
export async function retryWithoutListingType<R extends WriteResult>(
  write: (withListingType: boolean) => PromiseLike<R>,
  onMissing: (error: WriteResult["error"]) => void,
): Promise<R> {
  const result = await write(true);
  if (!isMissingListingTypeColumn(result.error)) return result;
  onMissing(result.error);
  return write(false);
}

/** The same row without listing_type, for retryWithoutListingType. */
export function withoutListingType<T extends { listing_type?: unknown }>(
  row: T,
): Omit<T, "listing_type"> {
  const { listing_type: _lane, ...rest } = row;
  return rest;
}
